import { ObjectType } from '@/@types/graphql-types';
import { ShareEvents } from '@/enums/events';
import { ShareMethod } from '@/enums/share-methods';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { addToast } from '@/helpers/toast/toast-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getVm } from '@/helpers/vm-helper';

export const ShareHelper = {
	async share(
		titleDetails: { titleId: number; titleObjectType: ObjectType; titleFullPath: string; titleName: string },
		shareMethod?: ShareMethod,
		additionalContexts?: SnowplowContext[]
	) {
		const shareUrl: string = getShareUrl(titleDetails.titleFullPath);
		const shareText: string = `${titleDetails.titleName}:` ?? document.title;

		if (process.server) {
			return;
		}
		const navigator: Navigator = window.navigator;
		if (navigator && navigator.share) {
			trackEvent(
				titleDetails.titleId,
				titleDetails.titleObjectType,
				ShareEvents.CLICKED,
				undefined,
				additionalContexts
			);
			navigator
				.share({
					url: shareUrl,
					text: shareText,
				})
				.then(() => {
					trackEvent(
						titleDetails.titleId,
						titleDetails.titleObjectType,
						ShareEvents.SHARED,
						undefined,
						additionalContexts
					);
				});
		} else {
			trackEvent(
				titleDetails.titleId,
				titleDetails.titleObjectType,
				ShareEvents.SHARED,
				shareMethod,
				additionalContexts
			);
			switch (shareMethod) {
				case ShareMethod.FACEBOOK:
					{
						const url: string = new URL(
							`?u=${shareUrl}&t=${shareText}`,
							'http://www.facebook.com/sharer/sharer.php'
						).toString();
						window.open(url);
					}
					break;
				case ShareMethod.TWITTER:
					{
						const url: string = new URL(
							`?url=${shareUrl}&text=${shareText}`,
							'https://twitter.com/intent/tweet'
						).toString();
						window.open(url);
					}
					break;
				default:
					if (navigator && navigator.clipboard) {
						navigator.clipboard.writeText(shareUrl);
						addToast({ message: getVm().$t('WEBAPP_COPIED_TO_CLIPBOARD') });
					}
			}
		}
	},
};

function trackEvent(
	titleId: number,
	titleObjectType: ObjectType,
	action: ShareEvents,
	label?: string,
	additionalContexts?: SnowplowContext[]
): void {
	const contexts: SnowplowContext[] = [
		new SnowplowTitleContextGraphql(titleId, titleObjectType),
		...(additionalContexts ? additionalContexts : []),
	];

	TrackingHelper.trackEvent(
		ShareEvents.CATEGORY,
		{
			action,
			label,
		},
		contexts
	);
}

function getShareUrl(titleFullPath: string): string {
	const standardWebURL = new URL(titleFullPath, `https://www.${JW_CONFIG.DOMAIN}`).href;
	return new ClickoutUrl({ standardWebURL }).set('utm_source', 'share').set('utm_medium', 'web').toString();
}
